import React, { useState, useContext, useEffect, useId, useRef, useCallback } from 'react';
import NewTaskModal from '../task/NewTaskModal';
import { KanbanListView } from './KanbanListView';
import { dueDateIso, isoFromToday, normalizeSearch, taskSearchHaystack, todayIso } from './kanbanRowValues';
import KanbanAssigneeFilter from './KanbanAssigneeFilter';
import KanbanSortableWrapper from './KanbanSortableWrapper';
import { setPolicyContext, canTransition, isStateClosed } from './kanbanTransitionPolicy';
import { notifyRefusal, classifyRefusal } from './kanbanNotify';
import KanbanToast from './KanbanToast';
import { tasksProcessor } from '../../dataProcessors/decoupledApiProcessors';
import { deleteTask, archiveTask, unarchiveTask } from '../../services/api';
import { readShowArchived } from '../../services/archive';
import { Archive, Search, X } from 'lucide-react';
import { generateKeyBetween, sortTasksByRankWithinColumns } from '../../utils/fractionalIndex';
import { getTagColorClass } from '../../utils/tagColors';
import {
  DndContext,
  pointerWithin,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDndMonitor,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
  useEvaluatedAttributes,
  ReactiveJsonSubroot,
} from "@ea-lab/reactive-json";

// Resolve a label to its taxonomy TID using the dynamic options arrays
// loaded from additionalDataSource (no more hardcoded TID maps).
function resolveOptionTid(label, options) {
  if (!label || typeof label !== 'string' || !Array.isArray(options)) return label;
  const match = options.find(o => o.label === label);
  return match ? match.value : label;
}

// Resolve a TID (or any raw value) to its label using dynamic options.
function resolveTypeLabel(tid, options) {
  if (tid == null || tid === '' || !Array.isArray(options)) return '';
  const match = options.find(o => String(o.value) === String(tid));
  return match ? match.label : String(tid);
}

/**
 * Build the single, unified set of kanban columns for the main board from the
 * workflow states of the visible task types.
 *
 * - When a specific type is selected, only that type's workflow states are used.
 * - Otherwise the UNION across all types is taken, deduplicated by state id and
 *   ordered by ascending weight (closed states pushed last on a multi-type board).
 * - No workflow state at all (e.g. a project with no task yet, so no task type is
 *   "present") => NO column: the board stays empty rather than falling back to the
 *   obsolete legacy task_status columns.
 */
function collectWorkflowStates(types) {
  const byId = new Map();
  types.forEach(tt => {
    const states = tt.data?.workflow?.workflowStates ?? {};
    Object.entries(states).forEach(([id, st]) => {
      const weight = st.weight ?? 0;
      // « Clos » = état terminal/annulé : repoussé en fin sur le board multi-type.
      const closed = !!(st.isClosed || st.isFinal);
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, { id, title: st.label ?? id, weight, closed });
      } else if (weight < existing.weight) {
        existing.weight = weight;
      }
    });
  });
  return byId;
}

function buildUnifiedColumns(workflowTaskTypes, selectedType) {
  const selected = selectedType
    ? workflowTaskTypes.filter(tt => String(tt.data.id) === String(selectedType))
    : workflowTaskTypes;

  let byId = collectWorkflowStates(selected);

  // If a specific type was selected but it defines no workflow states, fall back
  // to the union across all types so its stateful tasks still land in a visible
  // column ('__no_state__' or a shared state) instead of disappearing.
  if (byId.size === 0 && selectedType) {
    byId = collectWorkflowStates(workflowTaskTypes);
  }

  // Board MULTI-TYPE (union, aucun type sélectionné) : repousser les états CLOS
  // (terminal/annulé) en fin, pour ne pas intercaler des colonnes terminales au
  // milieu des états actifs d'un autre workflow (tri par poids fusionné). Pour un
  // type UNIQUE on respecte l'ordre de poids du workflow (voulu par l'auteur).
  const ordered = selectedType
    ? [...byId.values()].sort((a, b) => a.weight - b.weight)
    : [...byId.values()].sort((a, b) =>
        (a.closed === b.closed) ? a.weight - b.weight : (a.closed ? 1 : 0) - (b.closed ? 1 : 0));
  // No "Sans état" column: every task now carries a state (enforced at creation
  // + seeded + reset on incompatible type change), so the virtual stateless
  // column is no longer shown on the board.
  return [...ordered];
}

// Map a task to the column it belongs to within a given column set: its own
// columnId if that is a valid column, else the virtual '__no_state__' column
// (only when that column exists — otherwise the raw columnId, legacy behaviour).
function effectiveColId(task, validColIds) {
  const colId = task.data.columnId;
  if (validColIds.has(colId)) return colId;
  return validColIds.has('__no_state__') ? '__no_state__' : colId;
}

// Resolve the workflow {transitions, workflowStates} for a given task type id.
function getWorkflowForType(typeId, workflowTaskTypes) {
  const tt = workflowTaskTypes.find(t => String(t.data.id) === String(typeId));
  return {
    transitions: tt?.data?.workflow?.workflowTransitions ?? null,
    workflowStates: tt?.data?.workflow?.workflowStates ?? null,
  };
}

/**
 * POST only the changed fields to a task's submission_url.
 * Sends fieldOverrides (not the full task.data) to avoid re-sending fields
 * not accepted by the API.
 *
 * When `state` is provided as a string workflow state machine ID, it is sent
 * directly (no TID resolution needed). Legacy `status` (taxonomy TID) is
 * resolved only when the task has no `state` field.
 *
 * @param {Object} task - Task object with data and submission_url.
 * @param {Object} fieldOverrides - Only the fields that actually changed.
 * @param {Object} options - Dynamic taxonomy options { statusOptions }.
 */
async function persistTask(task, fieldOverrides = {}, options = {}) {
  const payload = { ...fieldOverrides };

  // Prefer workflow state (string) over legacy taxonomy status (TID).
  if (!payload.state && payload.status && typeof payload.status === 'string') {
    payload.status = resolveOptionTid(payload.status, options.statusOptions);
  }

  for (const key of ['assignee', 'returnType', 'evolutionType']) {
    if (payload[key] === '') payload[key] = null;
  }

  const response = await fetch(task.submission_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: payload }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(`Task save failed (${response.status}): ${err?.status?.message || response.statusText}`);
  }
  return response.json();
}

/**
 * Map a persistTask() server failure to a notify {reason, message}. The error
 * message embeds the HTTP status + the server's own (FR) message; the shared
 * classifyRefusal() strips the technical prefix and derives the icon/label.
 */
function deriveNotifyFromError(err) {
  return classifyRefusal(err?.message);
}

// Short FR motif shown INSIDE the "drop forbidden" column overlay during a drag.
// Kept terse (the full sentence is reserved for the toast on an actual drop).
const FORBIDDEN_OVERLAY_LABEL = {
  workflow: 'Transition non prévue',
  permission: 'Rôle non autorisé',
  subtasks: 'Sous-tâches à terminer',
};

/**
 * For a column during a drag, return the short forbidden motif (string) when the
 * dragged task may NOT drop there, or false when it may. `colId` is also the
 * candidate target state. The virtual '__no_state__' column is always forbidden
 * on a workflow-managed board (clearing the state would crash the validator).
 */
function forbiddenMotif(activeTask, colId, hasWorkflow) {
  if (!activeTask) return false;
  // A card may always return to (or reorder within) its own current column —
  // never flag the source column as forbidden, even though no self-transition
  // exists in the workflow. The drop handler treats start===target as a no-op.
  if (colId !== '__no_state__' && activeTask.data?.state === colId) return false;
  if (colId === '__no_state__') {
    return hasWorkflow ? FORBIDDEN_OVERLAY_LABEL.workflow : false;
  }
  const verdict = canTransition(activeTask, colId);
  if (verdict.ok) return false;
  return FORBIDDEN_OVERLAY_LABEL[verdict.reason] || 'Déplacement impossible';
}

// --- Components ---

// StarRating, TagList, UserAvatar, and ListView extracted to KanbanListView.jsx.

// Full card rendered in the DragOverlay — same YAML as the board cards.
// pointer-events-none disables all interactions (stars, delete, etc.) during drag.
// ReactiveJsonSubroot keeps access to GlobalDataContext via DnD kit's portal.
/**
 * Rank droppables by distance to the POINTER.
 *
 * closestCenter ranks by distance to the dragged CARD's centre, which sits up to
 * ~150px from the cursor: in the 16px gap between two 50px strips it can hand
 * back a strip three columns away. The pointer is what the user aims with, so it
 * is what decides. Keyboard drags have no pointer — the caller keeps
 * closestCenter for those.
 */
function closestToPointer(args) {
  const { droppableContainers, droppableRects, pointerCoordinates } = args;
  const out = [];
  droppableContainers.forEach((c) => {
    const rect = droppableRects.get(c.id);
    if (!rect) return;
    const dx = pointerCoordinates.x - (rect.left + rect.width / 2);
    const dy = pointerCoordinates.y - (rect.top + rect.height / 2);
    out.push({ id: c.id, data: { droppableContainer: c, value: Math.sqrt(dx * dx + dy * dy) } });
  });
  return out.sort((a, b) => a.data.value - b.data.value);
}

/**
 * Colour language of a collapsed column while a card is being dragged:
 *  - allowed : the card may land here
 *  - refused : belongs to this type's workflow, but the move is blocked (banner
 *              carries the motive)
 *  - outside : not part of this type's workflow at all — this card is simply not
 *              concerned. Kept distinct from `refused` so red keeps meaning
 *              something: on a multi-type board most columns are outside.
 *  - null    : no drag in progress, ordinary board.
 */
function dropToneClass(dropTone, isOver) {
  // "You are here" wins over the tone, and is deliberately tone-INDEPENDENT: the
  // tone ring of a grey strip is slate-on-slate at 60% opacity, i.e. invisible —
  // the pointer's position and the permission are two different signals, and the
  // first one must read on every strip. Emitted as a single ring-colour class:
  // two of them (tone + focus) would fight over --tw-ring-color in stylesheet
  // order, not in attribute order. `relative` only carries z-10 — it changes no
  // geometry, so it cannot move a target under the pointer.
  if (isOver && dropTone) {
    const bg = dropTone === 'allowed' ? 'bg-emerald-50'
      : dropTone === 'refused' ? 'bg-rose-50'
      : 'bg-slate-100';
    return `${bg} ring-2 ring-iris-400 relative z-10`;
  }
  switch (dropTone) {
    case 'allowed': return 'bg-emerald-50 ring-2 ring-emerald-500';
    case 'refused': return 'bg-rose-50 ring-2 ring-rose-400';
    case 'outside': return 'bg-slate-100 ring-2 ring-slate-200 opacity-60';
    case 'current': return 'bg-slate-100 ring-2 ring-slate-300';
    default: return 'bg-slate-100 hover:bg-slate-200';
  }
}

/**
 * Fixed banner naming the column under the pointer, and saying whether the card
 * may land there — and if not, why.
 *
 * Why a banner rather than a message in the column: during a drag every column
 * but the source collapses to a ~50px strip (see isColCollapsedId), which has no
 * room for "Sous-tâches à terminer"; and expanding the hovered one to show it
 * would move the geometry mid-gesture — precisely how dnd-kit ends up stamping a
 * card into a column nobody aimed at. So the message lives here, motionless, and
 * the strips carry nothing but colour.
 *
 * It reads dnd-kit's `over` through useDndMonitor rather than through the board's
 * own onDragOver, so what it announces can never diverge from what a drop would
 * actually do.
 */
const DropTargetBanner = ({ activeTask, workflowTaskTypes, columns }) => {
  const [overId, setOverId] = useState(null);
  useDndMonitor({
    onDragOver: ({ over }) => setOverId(over?.id ?? null),
    onDragEnd: () => setOverId(null),
    onDragCancel: () => setOverId(null),
  });
  if (!activeTask) return null;
  const { transitions, workflowStates } = getWorkflowForType(activeTask.data?.type, workflowTaskTypes);
  const col = columns.find(c => c.id === overId);
  const inWorkflow = !!(col && workflowStates && workflowStates[col.id]);
  const isCurrent = !!col && activeTask.data?.state === col.id;
  const motif = col ? forbiddenMotif(activeTask, col.id, !!transitions) : false;

  let tone = 'text-slate-500';
  let message = 'Survolez une colonne pour choisir un état';
  if (col && !inWorkflow) {
    // A state outside this card's workflow is not an option at all: say nothing
    // about it. Naming every foreign state would be noise on a multi-type board —
    // the grey strip already says "not your concern".
  } else if (col && isCurrent) {
    tone = 'text-slate-600';
    message = `« ${col.title} » — état actuel de la tâche`;
  } else if (col && typeof motif === 'string') {
    tone = 'text-rose-600';
    message = `« ${col.title} » — ${motif}`;
  } else if (col) {
    tone = 'text-emerald-700';
    message = `Déposer ici : « ${col.title} »`;
  }
  return (
    <div className="absolute top-0 left-1/2 -translate-x-1/2 z-40 card px-4 py-2 pointer-events-none">
      <span className={`text-sm font-semibold whitespace-nowrap ${tone}`}>{message}</span>
    </div>
  );
};

/**
 * One state of the palette. A drop target in its own right — dnd-kit resolves
 * collisions from measured rects, not from hit-testing, so the palette can stay
 * pointer-events:none and never interfere with the board underneath.
 */
const DragPreviewCard = ({ task, users, tagOptions, returnTypeOptions, typeOptions, showDetails }) => {
  if (!task?.data) return null;

  const dataOverride = {
    apiData: {
      submission_url: task.submission_url,
      data: {
        title: task.data.title,
        priority: task.data.rating ?? 0,
        complexity: task.data.complexity ?? 0,
        tags: task.data.tags ?? [],
        isUrgent: task.data.isUrgent ?? false,
        estimation: task.data.estimation ?? 0,
        assignee: task.data.assignee || null,
      },
    },
    data: {
      id: task.data.id,
      type: task.data.type,
      title: task.data.title,
      demande_evolution: task.data.demande_evolution,
      assignee: task.data.assignee || null,
      isUrgent: task.data.isUrgent ?? false,
      showDetails: showDetails ?? true,  // compact flag — read via ~~.data.showDetails
      linkedContentRefs: task.data.linkedContentRefs ?? [],
      ref: task.data.ref ?? '',
    },
    delete_url: task.delete_url,
    users: users ?? [],
    tagOptions: tagOptions ?? [],
    returnTypeOptions: returnTypeOptions ?? [],
    typeOptions: typeOptions ?? [],
    subtaskCount: 0,
  };

  return (
    <div className="opacity-95 cursor-grabbing pointer-events-none">
      <ReactiveJsonSubroot
        props={{ rjOptions: { rjBuildUrl: '/components/KanbanCard.yaml', dataOverride } }}
      />
    </div>
  );
};

// React.memo: a single board can render 8+ columns; without memoization every
// optimistic setTasks during a drag re-renders ALL columns (and thus every card
// subroot), which is the #1 cause of drag lag. Memoizing means only the columns
// whose props actually changed (the source + hovered target) re-render. Props
// are stable references (callbacks via useCallback, option arrays unchanged
// between renders) so the default shallow compare is correct here.
const Column = React.memo(({ column, tasks, showCardDetails, isDropForbidden, isDropAllowed, dropTone, recentMoveId, allTasks, isCollapsed, toggleCollapse, tagOptions = [], returnTypeOptions = [], typeOptions = [], users = [], widthClass = 'w-[86vw] max-w-[300px] sm:w-[300px]' }) => {
  const {
    setNodeRef,
    isOver,
  } = useDroppable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    },
  });

  const tasksIds = tasks.map((task) => task.data.id);

  const isDropDisabled = false;

  if (isCollapsed) {
    return (
      <div
        ref={setNodeRef}
        // During a drag these strips ARE the drop targets: colour says whether
        // the card may land here, the banner says why. Everything is expressed
        // with `ring` (a box-shadow, outside the box) — never a border or a
        // scale, which would change the MEASURED rect and slide the target out
        // from under the pointer, the root of every drag bug in this board.
        // transition-colors (NOT transition-all) while a card is in hand: at
        // pick-up this column collapses 300px -> 50px, and `transition-all` would
        // ANIMATE that width. dnd-kit measures its drop rects in the commit that
        // collapses the board — i.e. mid-animation — so it would keep comparing
        // the pointer against a board still 300px per column: an error of zero on
        // the left, growing with every column to the right, until the highlight
        // lands hundreds of pixels away from the cursor. Geometry must be FINAL in
        // the commit that gets measured; colours may keep animating.
        className={`${dropToneClass(dropTone, isOver)} w-[50px] self-stretch min-h-[160px] max-h-[600px] rounded-md flex flex-col m-1 flex-shrink-0 items-center py-4 cursor-pointer border border-slate-200 ${dropTone ? 'transition-colors' : 'transition-all'} ${
           isDropDisabled ? 'opacity-50' : ''
        }`}
        onClick={() => toggleCollapse && toggleCollapse(column.id)}
        title={column.title}
      >
          <div className="mt-2 text-[10px] font-bold bg-slate-200 rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center text-slate-600 mb-4">
              {tasks.length}
          </div>
          <div className="text-slate-500 font-bold tracking-wide whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {column.title}
          </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      // Green surface + ring when the dragged card MAY land here — the mirror of
      // the red overlay below. A ring is drawn outside the box, so it never moves
      // the column (see the collapsed branch for why that matters).
      className={`${isDropAllowed ? 'bg-emerald-50 ring-2 ring-emerald-500' : 'bg-slate-50'} ${widthClass} min-h-[200px] rounded-md flex flex-col m-1 flex-shrink-0 ${
        isDropDisabled ? 'opacity-50 bg-slate-200 border-rose-200 border' : ''} relative`}
    >
        {isDropForbidden && (
            <div className="absolute inset-0 bg-rose-100/50 z-50 flex flex-col items-center justify-center rounded-md pointer-events-none border-2 border-rose-500">
                <div className="bg-white p-3 rounded-full shadow-lg mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="text-rose-600" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                </div>
                {typeof isDropForbidden === 'string' && (
                     <div className="bg-white px-3 py-1.5 rounded-md shadow-sm text-rose-600 font-semibold text-sm border border-rose-200">
                        {isDropForbidden}
                     </div>
                )}
            </div>
        )}
      <div
        className="px-3 py-2.5 font-bold text-slate-700 flex items-center justify-between border-b border-slate-200 border-b-2 relative"
      >
        <div className="flex flex-col w-full">
            <div className="flex justify-between items-center w-full">
                 <div className="flex items-center gap-2">
                    <span>{column.title}</span>
                    <span className="text-slate-400 text-xs font-normal">{tasks.length}</span>
                 </div>
                 <button 
                    onClick={() => toggleCollapse && toggleCollapse(column.id)}
                    className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-200"
                    title="Réduire la colonne"
                 >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/></svg>
                 </button>
            </div>
            {/* Colored bar */}
            <div className={`absolute bottom-0 left-0 h-0.5 w-16 ${
                column.title === 'Etape 1' ? 'bg-slate-500' :
                column.title === 'Etape 2' ? 'bg-slate-500' :
                column.title === 'Etape 3' ? 'bg-slate-500' :
                'bg-transparent'
            }`}></div>
        </div>
      </div>

      <div className="p-2 overflow-x-hidden">
        <SortableContext items={tasksIds}>
          {tasks.map((task) => {
            const subtaskCount = allTasks ? allTasks.filter(t => t.data.parentId === task.data.id).length : 0;
            return (
              <KanbanSortableWrapper
                key={task.data.id}
                task={task}
                users={users}
                tagOptions={tagOptions}
                returnTypeOptions={returnTypeOptions}
                typeOptions={typeOptions}
                showDetails={showCardDetails}
                subtaskCount={subtaskCount}
                isRecentlyMoved={recentMoveId === task.data.id}
              />
            );
          })}
          <div className="h-28" aria-hidden="true" />
        </SortableContext>
      </div>
    </div>
  );
});
Column.displayName = 'Column';

export const KanbanBoard = ({ props, currentData, path }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const mainAttributesHolderRef = useRef(null);
  const attributes = useEvaluatedAttributes(props.attributes);

  // Tasks
  let evaluatedTasks = [];
  
  // 1. Try to evaluate props.tasks
  const rawEvaluatedTasks = evaluateTemplateValue({
        valueToEvaluate: props.tasks,
        globalDataContext,
        templateContext
    });

  if (Array.isArray(rawEvaluatedTasks)) {
      evaluatedTasks = rawEvaluatedTasks;
  }
  
  // 2. Fallback: Check if we got a reference string that wasn't resolved, or empty result
  // and try to fetch directly from global data if available
  if (evaluatedTasks.length === 0) {
       if (globalDataContext?.data?.tasks && Array.isArray(globalDataContext.data.tasks)) {
          evaluatedTasks = globalDataContext.data.tasks;
       }
  }

  // 3. Last resort: check if props.tasks was literally the array (unlikely with ReactiveJson but possible)
  if (evaluatedTasks.length === 0 && Array.isArray(props.tasks)) {
      evaluatedTasks = props.tasks;
  }


  // Populate taskCollectionUrl for fetchData reactions.
  // globalDataContext exposes templateData (not .data), and the project ID is
  // reliably available via the URL parameter used by additionalDataSource.
  useEffect(() => {
    const projectId = new URLSearchParams(window.location.search).get('id');
    if (!projectId || !globalDataContext?.updateData) return;
    const currentUrl = globalDataContext.templateData?.taskCollectionUrl;
    if (!currentUrl) {
      // include_archived=1 so archived tasks are always fetched; the board hides
      // them client-side unless the "Voir les archivées" toggle is on (access is
      // still enforced server-side, so this never leaks tasks outside the group).
      const url = `/decoupled-api/node/task/collection?display=decoupled_card&filter[0][f]=field_project&filter[0][v]=${projectId}&include_archived=1`;
      globalDataContext.updateData(url, "taskCollectionUrl");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Users
  let users = evaluateTemplateValue({
      valueToEvaluate: props.users,
      globalDataContext,
      templateContext
  });
  if (!users || (typeof users === 'string' && users.startsWith('~'))) {
      if (globalDataContext?.data?.users) {
          users = globalDataContext.data.users;
      }
  }
  users = Array.isArray(users) ? users : [];

  // Users with access to the project (members), used to scope the assignee
  // filter. Falls back to all users when the project has no members so the
  // filter never becomes empty/unusable. `users` itself stays unscoped (the
  // board/cards still need every author/assignee that appears on a task).
  // Read project memberIds reactively (the GlobalDataContext has no `.data`
  // member — only the store/templateData; `~~.project.memberIds` resolves via
  // singleProjectProcessor's output on the kanban page).
  const rawProjectMemberIds = evaluateTemplateValue({ valueToEvaluate: '~~.project.memberIds', globalDataContext, templateContext });
  const projectMemberIds = Array.isArray(rawProjectMemberIds) ? rawProjectMemberIds : [];
  const memberUsers = projectMemberIds.length
      ? users.filter(u => projectMemberIds.includes(Number(u.id)))
      : users;

  // Tags: prefer dynamic tagOptions from API, fallback to static props.tags
  let tagOptions = evaluateTemplateValue({
      valueToEvaluate: props.tagOptions,
      globalDataContext,
      templateContext
  });
  tagOptions = Array.isArray(tagOptions) ? tagOptions : [];

  let availableTags;
  if (tagOptions.length > 0) {
      availableTags = tagOptions.map(o => o.label).filter(Boolean);
  } else {
      availableTags = evaluateTemplateValue({
          valueToEvaluate: props.tags,
          globalDataContext,
          templateContext
      });
      if (!availableTags || (typeof availableTags === 'string' && availableTags.startsWith('~'))) {
          if (globalDataContext?.data?.tags) {
              availableTags = globalDataContext.data.tags;
          }
      }
      availableTags = Array.isArray(availableTags) ? availableTags : [];
  }

  let returnTypeOptions = evaluateTemplateValue({
      valueToEvaluate: props.returnTypeOptions,
      globalDataContext,
      templateContext
  });
  returnTypeOptions = Array.isArray(returnTypeOptions) ? returnTypeOptions : (globalDataContext?.data?.returnTypeOptions ?? []);

  let evolutionTypeOptions = evaluateTemplateValue({
      valueToEvaluate: props.evolutionTypeOptions,
      globalDataContext,
      templateContext
  });
  evolutionTypeOptions = Array.isArray(evolutionTypeOptions) ? evolutionTypeOptions : (globalDataContext?.data?.evolutionTypeOptions ?? []);

  let statusOptions = evaluateTemplateValue({
      valueToEvaluate: props.statusOptions,
      globalDataContext,
      templateContext
  });
  statusOptions = Array.isArray(statusOptions) ? statusOptions : (globalDataContext?.data?.statusOptions ?? []);

  let typeOptions = evaluateTemplateValue({
      valueToEvaluate: props.typeOptions,
      globalDataContext,
      templateContext
  });
  typeOptions = Array.isArray(typeOptions) ? typeOptions : (globalDataContext?.data?.typeOptions ?? []);

  let rawTaskTypes = evaluateTemplateValue({
      valueToEvaluate: props.taskTypes,
      globalDataContext,
      templateContext
  });
  // Drill-down state: while set, the board shows THAT task's subtasks instead of
  // the project's tasks. Declared here because the whole scope below derives
  // from it.
  const [selectedParentTask, setSelectedParentTask] = useState(null);

  const taskTypesRaw = Array.isArray(rawTaskTypes) ? rawTaskTypes : [];
  // The board's SCOPE — the single notion the drill-down rests on: the project's
  // tasks, or, once drilled into a parent, that parent's children. Present types,
  // columns and the empty state all derive from it, so the drill-down IS the main
  // board, only scoped differently — no second board, no second drag engine.
  const scopedTasks = selectedParentTask
    ? (evaluatedTasks || []).filter(t => String(t?.data?.parentId) === String(selectedParentTask.data.id))
    : (evaluatedTasks || []);
  // Task types actually PRESENT in that scope. The board is scoped, so neither
  // the type filter nor the unified columns may advertise types the scope never
  // uses (the task_type taxonomy is global). Derived from the loaded tasks, so it
  // follows creations/deletions on the next refetch. Archived tasks count too, so
  // the list stays stable whatever the "Voir les archivées" toggle.
  const presentTypeIds = new Set(
    scopedTasks.map(t => String(t?.data?.type ?? '')).filter(Boolean)
  );
  // Nothing in scope (project without a task, parent without a subtask) → no
  // present type → no column, by design. The board then shows an empty state
  // inviting creation rather than a blank area. Read from the LOADED tasks (not
  // the `tasks` state, which is [] on first render) so it never flashes on a
  // scope that does have tasks.
  const scopeHasNoTask = scopedTasks.length === 0;
  // Project view: only top-level types (parentId === 0) THAT THIS PROJECT USES
  // get a filter entry / board columns.
  // Drill-down: the scope is a parent's children, so the types that matter are
  // THEIR (child) types — the parentId === 0 rule would exclude every one of them
  // and leave the board without a single column.
  // Sub-task view (isSubTaskView): all passed types are child terms of the
  // current task — already scoped by TaskEdit, so keep them all.
  const workflowTaskTypes = taskTypesRaw.filter(
    tt => tt?.data?.name && tt?.data?.id != null &&
      (props.isSubTaskView
        ? true
        : selectedParentTask
          ? presentTypeIds.has(String(tt.data.id))
          : (tt?.data?.parentId ?? 0) === 0 && presentTypeIds.has(String(tt.data.id)))
  );
  // ALL task types (parents AND child/sub types) for the workflow store, so the
  // list-mode Status cell can resolve SUBTASK transitions/labels. Kept SEPARATE
  // from workflowTaskTypes (parents-only), which still drives buildUnifiedColumns
  // / displayColumns — the board columns must stay unchanged.
  const storeWorkflowTypes = taskTypesRaw.filter(tt => tt?.data?.name && tt?.data?.id != null);

  // CDC-TMA recette 3: ALL of the current user's group_role machine ids in this
  // project (cumulative), exposed by GET /api/project/{id}/role as `roles: []`.
  // Drives the permission pre-filter in kanbanTransitionPolicy. Falls back to an
  // empty array when the contract is not yet deployed (the policy then tolerates
  // missing allowedRoles so nothing gets masked).
  let currentUserRoles = evaluateTemplateValue({
      valueToEvaluate: props.currentUserRoles,
      globalDataContext,
      templateContext
  });
  if (!Array.isArray(currentUserRoles)) {
      currentUserRoles = globalDataContext?.data?.currentUserRoleData?.roles ?? [];
  }
  currentUserRoles = Array.isArray(currentUserRoles) ? currentUserRoles : [];


  // Initial state from props or empty
  const [tasks, setTasks] = useState([]);
  // "Voir les archivées" toggle (shared 'orbit-show-archived' key with docs /
  // projects), flipped by the KanbanHeader button via a CustomEvent. When off,
  // archived tasks are filtered out of the board/list below.
  const [showArchived, setShowArchived] = useState(readShowArchived);
  const [activeTask, setActiveTask] = useState(null);
  // CDC-TMA T18: default to the list view on mobile (the Kanban board is
  // unusable on small screens); remember the user's explicit choice in
  // localStorage (until the server-side prefs of T31 land). The sub-task view
  // key so it never collides with the main board's preference; it defaults to
  // 'board' (no mobile auto-list there — the fiche is a contained panel).
  const viewModeStorageKey = props.isSubTaskView ? 'orbit-subtask-view-mode' : 'orbit-kanban-view-mode';
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'board';
    try {
      const saved = window.localStorage.getItem(viewModeStorageKey);
      if (saved === 'board' || saved === 'list') return saved;
    } catch { /* ignore */ }
    if (props.isSubTaskView) return 'board';
    return window.innerWidth < 768 ? 'list' : 'board';
  }); // 'board' or 'list'

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(viewModeStorageKey, viewMode); } catch { /* ignore */ }
  }, [viewMode, viewModeStorageKey]);
  const [showCardDetails, setShowCardDetails] = useState(true);
  const [isSubTaskMenuOpen, setIsSubTaskMenuOpen] = useState(false);

  // Filters
  const [selectedType, setSelectedType] = useState(null);
  // Multi-select assignee filter (CDC-TMA T03): holds the selected user ids; a
  // card matches when its assignee resolves to one of them (empty set = no filter).
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  // Filtre plein texte : porte sur toute la ligne visible (titre, titre du
  // parent, type, état, assigné, type de retour, tags). Il siège dans la MÊME
  // barre que les filtres type/assigné, donc il restreint les DEUX vues.
  const [searchText, setSearchText] = useState('');
  const searchFieldId = useId();
  // Filtre d'échéance (field_due_date) — dans la MÊME barre que les autres,
  // donc il restreint la grille ET la liste : ne garder que les tâches à faire
  // au plus tard ce jour-là (inclus). Une tâche SANS échéance en sort — « à
  // faire avant le X » ne peut rien affirmer d'une tâche qui n'a pas de date.
  const [dueBefore, setDueBefore] = useState('');
  const dueBeforeFieldId = useId();
  const [collapsedColumns, setCollapsedColumns] = useState({});

  // Single source of truth for the main board columns: the union of the workflow
  // states of the task types PRESENT in this project (see buildUnifiedColumns).
  // No present type (empty project) => no column at all. Both the render AND the
  // drag-and-drop handlers below read this — they must never diverge.
  const displayColumns = buildUnifiedColumns(workflowTaskTypes, selectedType);
  const displayColColIds = new Set(displayColumns.map(c => c.id));

  // New state for sub-task modal

  // When drilling into a parent task's subtasks via the evolution button,
  // compute the subtype list from typeOptions.
  const subtaskTypes = selectedParentTask
    ? typeOptions.filter(o => Number(o.data?.parentId) === Number(selectedParentTask.data.type))
    : [];
  const [isSubTaskModalOpen, setIsSubTaskModalOpen] = useState(false);
  const [subTaskType, setSubTaskType] = useState('');

  // Drag-and-drop persistence
  const dragStartColumnRef = useRef(null);

  // Scroll preservation
  const scrollContainerRef = useRef(null);
  const mainBoardScrollPos = useRef(0);
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);

  // Restore scroll position when returning to main board
  useEffect(() => {
      if (shouldRestoreScroll && !selectedParentTask && scrollContainerRef.current) {
          // Use setTimeout to allow DOM to update width with new columns
          setTimeout(() => {
              if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollLeft = mainBoardScrollPos.current;
              }
          }, 100);
          setShouldRestoreScroll(false);
      }
  }, [displayColumns, shouldRestoreScroll, selectedParentTask]);

  useEffect(() => {
    setTasks(prevTasks => {
        if (prevTasks.length === 0) {
            // Initial load: sort tasks by rank within each column so that
            // the saved ordering is restored immediately on page load.
            return sortTasksByRankWithinColumns(evaluatedTasks);
        }

        const prevIds = new Set(prevTasks.map(t => t.data.id));
        const evaluatedIds = new Set(evaluatedTasks.map(t => t.data.id));

        const newTasks = evaluatedTasks.filter(t => !prevIds.has(t.data.id));
        const removedIds = prevTasks.filter(t => !evaluatedIds.has(t.data.id)).map(t => t.data.id);

        if (newTasks.length === 0 && removedIds.length === 0) {
            return prevTasks;
        }

        const keptTasks = prevTasks.filter(t => !removedIds.includes(t.data.id));

        // New tasks have no rank yet — append them after the ranked tasks
        return [...keptTasks, ...newTasks];
    });
  }, [evaluatedTasks, globalDataContext?.data?.tasks]);

  // CDC-TMA recette 3: seed the transition policy with everything it needs to
  // pre-filter moves (per-type workflows incl. subtype workflows, the user's
  // cumulative project roles, and the loaded tasks for the subtask-closure
  // guard). Replaces the old setWorkflowTypes(store) seed — the policy module
  // re-exports getStateLabel/getTransitionTargets, so card/list subroots keep
  // working through the kanbanWorkflowStore compat shim. Re-runs whenever the
  // types, the roles, or the task set change.
  useEffect(() => {
    setPolicyContext({ taskTypes: storeWorkflowTypes, userRoles: currentUserRoles, allTasks: tasks });
  }, [storeWorkflowTypes, currentUserRoles, tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // 3px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const measuringConfig = React.useMemo(() => ({
    droppable: { strategy: MeasuringStrategy.WhileDragging },
  }), []);

  // Prefer pointer-based detection (cursor inside a column/card), fall back
  // to closestCenter when the pointer isn't inside any droppable.
  const collisionDetection = React.useCallback((args) => {
    const hits = pointerWithin(args);
    // ARMING — at pick-up every column but the source collapses, so the board is
    // re-laid out UNDER a pointer that has not moved: some column lands beneath
    // the cursor without ever having been aimed at, and merely releasing would
    // persist its state. dnd-kit recomputes `over` from the re-measured rects and
    // the LAST KNOWN pointer position, so nothing in the library protects us.
    //
    // Rule: a target only counts once the pointer has been OUTSIDE it, after
    // having moved — i.e. it must be ENTERED from the outside, like any hover
    // target. Whatever the collapse drops under the cursor stays inert until the
    // cursor leaves it; everything else is armed on the first move.
    if (pointerMovedRef.current) {
      const inside = new Set(hits.map(h => h.id));
      args.droppableContainers.forEach(c => {
        if (!inside.has(c.id)) leftDroppablesRef.current.add(c.id);
      });
    }
    const armed = (list) => list.filter(h => leftDroppablesRef.current.has(h.id));
    // Guarded on `hits`, NOT on the armed ones: the source column sits under the
    // pointer from pick-up, so it can never arm itself — testing the armed list
    // here would send every drag into the fallback below while the pointer is
    // still inside its own column, and closestCenter would hand back a target it
    // never aimed at. Inside something un-armed = no target, and that's correct.
    if (hits.length > 0) return armed(hits);
    // Pointer inside nothing (a gap between strips — ~25% of the collapsed row):
    // nearest armed container TO THE POINTER, never to the dragged card's centre.
    // The banner names it, so nothing is picked silently.
    return armed(args.pointerCoordinates ? closestToPointer(args) : closestCenter(args));
  }, []);

  // Track recent overId values to detect A→B→A oscillation in collision detection.
  const dragOverHistoryRef = useRef([]);

  // ARMING state (see collisionDetection): droppables the pointer has been
  // outside of at least once since it started moving, and whether it moved at all.
  const leftDroppablesRef = useRef(new Set());
  const pointerMovedRef = useRef(false);

  // "Where did my card land?" — a card dropped into ANOTHER column jumps to the
  // top of what was, until the drop, a collapsed strip. Ring it for 10s so the
  // eye can catch up; starting another drag clears it earlier.
  const [recentMoveId, setRecentMoveId] = useState(null);
  useEffect(() => {
    if (!recentMoveId) return undefined;
    const timer = setTimeout(() => setRecentMoveId(null), 10000);
    return () => clearTimeout(timer);
  }, [recentMoveId]);

  const pendingSavesRef = useRef({});
  // Archived project = read-only (a banner says so, and the server enforces it).
  // Declared here rather than next to the render: the event listeners below gate
  // on it too, and an action must never be offered where it cannot be performed.
  const projectArchived = globalDataContext?.data?.project?.archived || null;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  // Same trick as tasksRef: the column set is rebuilt on every render, so an
  // event listener that depended on it would re-subscribe on every render. The
  // ref lets the kanbanMoveToTop handler map a task to its column exactly like
  // the render does, without owning a stale copy.
  const displayColColIdsRef = useRef(displayColColIds);
  displayColColIdsRef.current = displayColColIds;

  const typeOptionsRef = useRef(typeOptions);
  typeOptionsRef.current = typeOptions;

  useEffect(() => {
    return () => {
      Object.values(pendingSavesRef.current).forEach(clearTimeout);
    };
  }, []);

  // Accumulate pending field changes per task so persistTask sends only what changed.
  const pendingUpdatesRef = useRef({});

  const updateTask = (taskId, updates) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.data.id === taskId
          ? { ...task, data: { ...task.data, ...updates } }
          : task
      )
    );
    if (globalDataContext?.updateData) {
      const storeTasks = globalDataContext.templateData?.tasks || [];
      const idx = storeTasks.findIndex(t => t.data.id === taskId);
      if (idx >= 0) {
        const current = storeTasks[idx];
        const updated = { ...current, data: { ...current.data, ...updates } };
        const newTasks = [...storeTasks];
        newTasks[idx] = updated;
        globalDataContext.updateData(newTasks, "tasks");
      }
    }

    pendingUpdatesRef.current[taskId] = {
      ...(pendingUpdatesRef.current[taskId] || {}),
      ...updates,
    };

    if (pendingSavesRef.current[taskId]) {
      clearTimeout(pendingSavesRef.current[taskId]);
    }
    pendingSavesRef.current[taskId] = setTimeout(() => {
      delete pendingSavesRef.current[taskId];
      const task = tasksRef.current.find(t => t.data.id === taskId);
      const accumulated = pendingUpdatesRef.current[taskId] || {};
      delete pendingUpdatesRef.current[taskId];
      if (task?.submission_url && Object.keys(accumulated).length > 0) {
        // PERF (recette 3): no global refetch after an inline auto-save — the edit
        // is already applied optimistically to both `tasks` and the global store
        // above, so refetching just re-renders the whole board for nothing.
        persistTask(task, accumulated, { statusOptions })
          .catch(err => console.error('Error auto-saving task:', err));
      }
    }, 2000);
  };

  const refreshTasks = useCallback(async () => {
    // templateData is the correct accessor for GlobalDataContext (no .data property)
    const url = globalDataContext?.templateData?.taskCollectionUrl;
    if (!url) return;
    try {
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) return;
      const raw = await response.json();
      const processed = tasksProcessor({
        requestContext: { url },
        responseContext: { status: 200 },
        dataToProcess: raw,
      });
      if (globalDataContext.updateData) {
        globalDataContext.updateData(Array.isArray(processed) ? processed : [], "tasks");
      }
    } catch (err) {
      console.error('Error refreshing tasks:', err);
    }
  }, [globalDataContext]);

  const handleDeleteTask = async (task) => {
    const tid = task.data.id;
    if (pendingSavesRef.current[tid]) {
      clearTimeout(pendingSavesRef.current[tid]);
      delete pendingSavesRef.current[tid];
    }
    try {
      await deleteTask(tid);
      setTasks(prev => prev.filter(t => t.data.id !== tid));
      const projectId = globalDataContext?.data?.project?.id
        || new URLSearchParams(window.location.search).get('id')
        || null;
      window.dispatchEvent(new CustomEvent('task:entity-trashed', {
        detail: { type: 'task', id: tid, projectId: projectId ? parseInt(projectId) : null },
      }));
      if (typeof props.onTaskDeleted === 'function') {
        props.onTaskDeleted(tid);
      }
      refreshTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  function onDragStart(event) {
    dragOverHistoryRef.current = [];
    // The board collapses in this very commit: nothing is a target until the
    // pointer has moved and proven it left it (see collisionDetection).
    leftDroppablesRef.current = new Set();
    pointerMovedRef.current = false;
    // Starting another drag IS "another action": drop the landing marker.
    setRecentMoveId(null);
    if (event.active.data.current?.type === 'Task') {
      const task = event.active.data.current.task;
      setActiveTask(task);
      dragStartColumnRef.current = effectiveColId(task, displayColColIds);
      // ...except the card's OWN column, armed from the start — and armed HERE,
      // after the ref above is assigned: on entry it is always null (every drag
      // end clears it), so arming it any earlier is a no-op. The source is
      // legitimately under the pointer at pick-up and is the one target that can
      // never cause an unwanted transition (dropping home only reorders). Leaving
      // it inert strands `over` at null inside the source: a reorder released on
      // the empty space below the cards would then display a move that nothing
      // persists.
      leftDroppablesRef.current.add(dragStartColumnRef.current);
      // The forbidden-drop overlay is computed per-column at render time via
      // forbiddenMotif(activeTask, …), which runs the full policy pre-filter
      // (workflow reachability + role + open subtasks) — no per-drag reachability
      // set to maintain here anymore.
      return;
    }
  }

  // A keyboard-cancelled drag (Escape) fires onDragCancel, not onDragEnd, so
  // reset the drag state here too to avoid leaving stale forbidden overlays.
  function onDragCancel() {

    setActiveTask(null);
    dragStartColumnRef.current = null;
  }

  function onDragEnd(event) {
    const { active, over } = event;

    setActiveTask(null);

    if (!over) { dragStartColumnRef.current = null; return; }

    const activeId = active.id;
    // tasksRef.current reflects the state after onDragOver's arrayMove calls
    const movedTask = tasksRef.current.find(t => t.data.id === activeId);
    if (!movedTask) { dragStartColumnRef.current = null; return; }

    const startColId = dragStartColumnRef.current;
    // Released ON a column: that column IS the target. onDragOver deliberately
    // does not stamp collapsed strips (see there), so the card's columnId still
    // says "source" — `over` is the only truth here. Released on a sibling card
    // (reordering inside the open source column), the stamped columnId is right.
    // Either way, normalise through effectiveColId: never a raw off-column id.
    const overColId = over.data.current?.type === 'Column' ? over.id : null;
    const targetColId = overColId ?? effectiveColId(movedTask, displayColColIds);
    const movedToNewColumn = startColId !== null && targetColId !== startColId;
    // Origin state captured up-front so a server refusal (see persist .catch
    // below) can revert BOTH the column and the workflow state to where the card
    // started — not just the optimistic columnId.
    const fromState = movedTask.data.state;

    // Full pre-filter (per the task's OWN type workflow + role permission + open
    // subtasks): block + revert a cross-column move the server would reject, and
    // explain why. The column id IS the target state id on a workflow board, so
    // targetColId doubles as toState. '__no_state__' clears the state via DnD,
    // which would crash TransitionValidator — always block it.
    if (movedToNewColumn) {
      const { transitions } = getWorkflowForType(movedTask.data.type, workflowTaskTypes);
      const isNoStateBlocked = targetColId === '__no_state__' && !!transitions;
      const verdict = isNoStateBlocked
        ? { ok: false, reason: 'workflow', message: 'Déplacement impossible : transition non prévue par le workflow de ce type de tâche.' }
        : canTransition(movedTask, targetColId);
      if (!verdict.ok) {
        setTasks(prev => prev.map(t =>
          t.data.id === activeId ? { ...t, data: { ...t.data, columnId: startColId } } : t
        ));
        notifyRefusal({ reason: verdict.reason, message: verdict.message });
        dragStartColumnRef.current = null;
        return;
      }
    }

    // Dropped on a collapsed strip: nothing previewed it, so move the card there
    // now — at the TOP of the column, which is also where the rank computed below
    // puts it (a strip shows no neighbours, so there is no position to honour).
    // The array order IS the render order, so moving the item in the array is
    // what actually puts the card on top; the rank alone would only show after a
    // full reload.
    if (overColId && movedToNewColumn) {
      setTasks(prev => {
        const next = prev.map(t =>
          t.data.id === activeId ? { ...t, data: { ...t.data, columnId: overColId } } : t
        );
        const from = next.findIndex(t => t.data.id === activeId);
        const firstOfTarget = next.findIndex(t =>
          t.data.id !== activeId && effectiveColId(t, displayColColIds) === overColId
        );
        if (from === -1 || firstOfTarget === -1) return next; // empty target column
        return arrayMove(next, from, firstOfTarget > from ? firstOfTarget - 1 : firstOfTarget);
      });
      setRecentMoveId(activeId);
    }

    const fieldOverrides = {};

    // Compute fractional rank based on the task's new neighbors in its column.
    // Group by EFFECTIVE column so all cards visible together in '__no_state__'
    // (which may carry differing raw columnIds) are considered neighbours.
    const columnTasks = tasksRef.current.filter(t => effectiveColId(t, displayColColIds) === targetColId);
    const taskIdx = columnTasks.findIndex(t => t.data.id === activeId);

    const prevRank = taskIdx > 0 ? (columnTasks[taskIdx - 1].data.rank ?? null) : null;
    const nextRank = taskIdx < columnTasks.length - 1 ? (columnTasks[taskIdx + 1].data.rank ?? null) : null;

    try {
      const newRank = generateKeyBetween(prevRank, nextRank);
      fieldOverrides.rank = newRank;
      // Update local state so the order is stable before the server responds
      setTasks(prev => prev.map(t =>
        t.data.id === activeId ? { ...t, data: { ...t.data, rank: newRank } } : t
      ));
    } catch (err) {
      console.error('Rank computation failed:', err);
    }

    // Persist the state change when the card changed column. Workflow columns
    // use string state ids (sent as `state`); '__no_state__' clears the state;
    // legacy numeric columns send the title as `status` (resolved to a TID).
    if (movedToNewColumn) {
      if (targetColId === '__no_state__') {
        fieldOverrides.state = '';
      } else {
        const col = displayColumns.find(c => c.id === targetColId);
        if (col) {
          if (typeof col.id === 'string') {
            fieldOverrides.state = col.id;
          } else {
            fieldOverrides.status = col.title;
          }
        }
      }
      if (fieldOverrides.state !== undefined) {
        setTasks(prev => prev.map(t =>
          t.data.id === activeId ? { ...t, data: { ...t.data, state: fieldOverrides.state } } : t
        ));
      }
    }

    if (Object.keys(fieldOverrides).length > 0) {
      // PERF (recette 3): no global refetch after a simple move — the optimistic
      // setTasks above already reflects the new column/rank, and a full refetch on
      // every drop was the #1 source of board lag. We only reconcile from the
      // server on a FAILURE (revert below); creations/deletions/archive keep their
      // legitimate refetch elsewhere.
      persistTask(movedTask, fieldOverrides, { statusOptions })
        .then(() => {
          // CASCADE re-sync (recette 3): only on a CLOSURE transition (target state
          // isClosed) does the server potentially cascade via ECA (e.g. a parent
          // unblocked/advanced when its last subtask closes). Refetch in that case
          // so the board reflects the side effect; normal moves keep the fast path.
          if (isStateClosed(movedTask.data.type, fieldOverrides.state)) refreshTasks();
        })
        .catch(err => {
          // BUG FIX (recette 3): a server refusal (e.g. 422/403) used to leave the
          // move applied locally with no feedback. Revert the card to its origin
          // column AND state, then surface the server's own (FR) message.
          console.error('Error persisting task:', err);
          setTasks(prev => prev.map(t =>
            t.data.id === activeId
              ? { ...t, data: { ...t.data, columnId: startColId, state: fromState } }
              : t
          ));
          // The card came back: there is no landing to point at anymore.
          setRecentMoveId(null);
          notifyRefusal(deriveNotifyFromError(err));
        });
    }

    dragStartColumnRef.current = null;
  }

  // dnd-kit only fires this on a real translate change, never on a re-measure —
  // which is exactly what "the pointer actually moved" means for the arming rule.
  function onDragMove() {
    pointerMovedRef.current = true;
  }

  function onDragOver(event) {
    const { active, over } = event;
    if (!over) return;

    // Every column but the source is a collapsed strip during a drag: it renders
    // no card, so there is nothing to preview there — and stamping the card into
    // it would pull it out of the (open) source column, whose height drives the
    // whole row through `self-stretch`: every strip would then shrink under the
    // pointer, mid-gesture. So only the source's own reordering is previewed
    // here; the drop target is read straight from `over` in onDragEnd.
    if (over.data.current?.type === 'Column' && over.id !== dragStartColumnRef.current) return;

    const activeId = active.id;
    const overId = over.id;

    // Detect A→B→A oscillation — collision detection flipping between two
    // targets on every render causes an infinite loop.
    const history = dragOverHistoryRef.current;
    if (history.length >= 2 &&
        history[history.length - 2] === overId &&
        history[history.length - 1] !== overId) {
      return;
    }
    history.push(overId);
    if (history.length > 6) history.splice(0, history.length - 6);

    if (activeId === overId) return;

    const isActiveTask = active.data.current?.type === 'Task';
    const isOverTask = over.data.current?.type === 'Task';

    if (!isActiveTask) return;

    // Optimistic reordering / cross-column move during drag. Workflow validity
    // is enforced once, on drop, by onDragEnd (which reverts invalid moves).
    if (isActiveTask && isOverTask) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.data.id === activeId);
        const overIndex = tasks.findIndex((t) => t.data.id === overId);
        if (activeIndex < 0 || overIndex < 0) return tasks;
        if (activeIndex === overIndex) return tasks;

        const targetColumnId = effectiveColId(tasks[overIndex], displayColColIds);

        // Compare effective-vs-effective: two cards sharing the same display
        // column (e.g. both in '__no_state__' with differing raw columnIds) must
        // take the same-column reorder path, not the cross-column move path.
        if (effectiveColId(tasks[activeIndex], displayColColIds) !== targetColumnId) {
            const newTasks = [...tasks];
            newTasks[activeIndex] = { ...newTasks[activeIndex], data: { ...newTasks[activeIndex].data, columnId: targetColumnId } };
            return arrayMove(newTasks, activeIndex, overIndex - 1);
        }

        return arrayMove(tasks, activeIndex, overIndex);
      });
    }

    const isOverColumn = over.data.current?.type === 'Column';

    if (isActiveTask && isOverColumn) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.data.id === activeId);
        if (activeIndex < 0) return tasks;

        const targetColumnId = overId;
        if (tasks[activeIndex].data.columnId === targetColumnId) return tasks;

        const newTasks = [...tasks];
        newTasks[activeIndex] = { ...newTasks[activeIndex], data: { ...newTasks[activeIndex].data, columnId: targetColumnId } };
        return arrayMove(newTasks, activeIndex, activeIndex);
      });
    }
  }

  // Same scope as the board itself (see scopedTasks): drives the per-column
  // counts and the "empty column → collapsed" default, which must count the
  // subtasks of the drilled-into parent — not the project's top-level tasks.
  const tasksForFilters = props.isSubTaskView
    ? tasks
    : selectedParentTask
      ? tasks.filter(t => String(t.data.parentId) === String(selectedParentTask.data.id))
      : tasks.filter(t => !typeOptions.some(o => String(o.value) === String(t.data.type) && Number(o.data?.parentId) > 0));
  // Resolve a task's assignee (stored as a user id OR a name) to a canonical
  // user id, so the assignee filter (which holds user ids) matches either form.
  const resolveAssigneeUserId = (assignee) => {
    if (assignee == null || assignee === '') return null;
    const u = users.find((uu) => String(uu.id) === String(assignee) || uu.name === assignee);
    return u ? String(u.id) : null;
  };

  // Un seul prédicat pour la grille ET la liste : une barre de filtres, un sens.
  // `parentTitle` est le contexte optionnel des lignes de sous-tâche en liste.
  const searchQuery = normalizeSearch(searchText.trim());
  const matchesSearch = (task, parentTitle) => !searchQuery || taskSearchHaystack(task, {
    users, tagOptions, typeOptions, returnTypeOptions, columns: displayColumns, parentTitle,
  }).includes(searchQuery);

  // Échéance : comparaison de CHAÎNES 'YYYY-MM-DD' (cf. kanbanRowValues) — même
  // convention que le back. Sans date saisie, tout passe ; sinon une tâche sans
  // échéance est écartée (elle n'est pas « à faire avant le X »).
  const matchesDue = (task) => {
    if (!dueBefore) return true;
    const due = dueDateIso(task.data.dueDate);
    return !!due && due <= dueBefore;
  };

  const filteredTasks = tasks.filter(task => {
      const d = task.data;
      const typeMatch = selectedType ? d.type === selectedType : true;
      const assigneeMatch = selectedAssignees.length ? selectedAssignees.includes(resolveAssigneeUserId(d.assignee)) : true;
      // Hide archived tasks unless "Voir les archivées" is on.
      const archivedMatch = showArchived || !d.archived;
      const searchMatch = matchesSearch(task);
      const dueMatch = matchesDue(task);

      if (props.isSubTaskView) {
          return archivedMatch && typeMatch && assigneeMatch && searchMatch && dueMatch;
      }

      // Compared as strings, like scopedTasks/tasksForFilters: these three MUST
      // agree — scopedTasks decides whether the board or the empty state renders,
      // and this one decides whether that board has any card. A single convention,
      // or a numeric/string mismatch on parentId would show a board of unfolded,
      // empty columns with no empty state.
      const contextMatch = selectedParentTask
        ? String(d.parentId) === String(selectedParentTask.data.id)
        : !typeOptions.some(o => String(o.value) === String(d.type) && Number(o.data?.parentId) > 0);

      return archivedMatch && contextMatch && typeMatch && assigneeMatch && searchMatch && dueMatch;
  });

  // ── List-mode dataset (S1+S2) — SEPARATE from filteredTasks so the board grid
  // stays 100% unchanged. Includes SUBTASKS (parity with cards in list mode),
  // keeps the archived / type(subtask-aware) / assignee filters, then groups each
  // parent immediately followed by its subtasks (indented). filteredTasks /
  // tasksForFilters / colTaskCounts are deliberately left untouched.
  const parentTitleById = new Map(tasks.map(t => [Number(t.data.id), t.data.title]));
  const parentTypeById = new Map(tasks.map(t => [Number(t.data.id), t.data.type]));
  const isChildType = (typeVal) => typeOptions.some(o => String(o.value) === String(typeVal) && Number(o.data?.parentId) > 0);

  const listBase = tasks.filter(task => {
    const d = task.data;
    const assigneeMatch = selectedAssignees.length ? selectedAssignees.includes(resolveAssigneeUserId(d.assignee)) : true;
    const archivedMatch = showArchived || !d.archived;
    // Subtask-aware: a child row matches when its OWN type OR its PARENT's type matches the selected type.
    const typeMatch = selectedType
      ? (String(d.type) === String(selectedType) || String(parentTypeById.get(Number(d.parentId))) === String(selectedType))
      : true;
    // Filtre structurel comme les précédents (et non une passe « texte » comme
    // la recherche) : une ligne parente écartée par l'échéance ne retient pas
    // ses sous-tâches, qui repartent en lignes orphelines de profondeur 1 —
    // exactement le sort déjà réservé au parent masqué par « archivées ».
    const dueMatch = matchesDue(task);
    if (props.isSubTaskView) {
      return archivedMatch && typeMatch && assigneeMatch && dueMatch;
    }
    // NB: no subtask-type exclusion here — that exclusion is only applied by filteredTasks for the grid.
    // Same string convention as the board scope above.
    const contextMatch = selectedParentTask
      ? (String(d.parentId) === String(selectedParentTask.data.id)
         || String(d.id) === String(selectedParentTask.data.id))
      : true;
    return archivedMatch && typeMatch && assigneeMatch && contextMatch && dueMatch;
  });

  // Passe plein texte, appliquée APRÈS les filtres structurels pour voir tout
  // l'ensemble survivant : une sous-tâche trouvée garde sa ligne parente (sinon
  // le « ↳ Sous-tâche de X » flotterait sans X au-dessus).
  const listSearched = !searchQuery ? listBase : (() => {
    const matchedIds = new Set(
      listBase
        .filter(t => matchesSearch(t, parentTitleById.get(Number(t.data.parentId))))
        .map(t => String(t.data.id)),
    );
    const keptParentIds = new Set(
      listBase
        .filter(t => matchedIds.has(String(t.data.id)) && t.data.parentId)
        .map(t => String(t.data.parentId)),
    );
    return listBase.filter(t => matchedIds.has(String(t.data.id)) || keptParentIds.has(String(t.data.id)));
  })();

  // Deterministic, stable ordering by task id (creation order) BEFORE grouping,
  // so the list never reshuffles on reload: the API fetch order can vary and many
  // tasks (especially subtasks) share or lack a column rank, which previously left
  // the order at the mercy of the (unstable) fetch order. The board grid is
  // unaffected — it keeps its own rank-within-column ordering.
  const byTaskId = (a, b) => {
    const na = Number(a.data.id), nb = Number(b.data.id);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a.data.id).localeCompare(String(b.data.id));
  };
  const sortedBase = [...listSearched].sort(byTaskId);

  let listTasks;
  if (props.isSubTaskView) {
    listTasks = sortedBase.map(t => ({ task: t, depth: 0, parentTitle: null }));
  } else {
    const parents = sortedBase.filter(t => !isChildType(t.data.type));
    const childrenByParent = new Map();
    sortedBase.filter(t => isChildType(t.data.type)).forEach(c => {
      const pid = Number(c.data.parentId);
      if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
      childrenByParent.get(pid).push(c);
    });
    const grouped = [];
    parents.forEach(p => {
      grouped.push({ task: p, depth: 0, parentTitle: null });
      (childrenByParent.get(Number(p.data.id)) || []).forEach(c => grouped.push({ task: c, depth: 1, parentTitle: parentTitleById.get(Number(c.data.parentId)) || '' }));
      childrenByParent.delete(Number(p.data.id));
    });
    // Orphan children whose parent is not in listBase (e.g. archived-hidden parent): append as depth-1 rows.
    childrenByParent.forEach(arr => arr.forEach(c => grouped.push({ task: c, depth: 1, parentTitle: parentTitleById.get(Number(c.data.parentId)) || '' })));
    listTasks = grouped;
  }

  // Structural card count per main-board column (ignores the type/assignee
  // filter so filtering never reshuffles the collapse layout; still reflects
  // optimistic columnId changes during a drag). Used to auto-collapse columns
  // that hold no card at all.
  const colTaskCounts = {};
  displayColumns.forEach(c => { colTaskCounts[c.id] = 0; });
  tasksForFilters.forEach(t => {
    const cid = effectiveColId(t, displayColColIds);
    if (cid in colTaskCounts) colTaskCounts[cid] += 1;
  });

  // Effective collapsed state of a main-board column:
  //  - a manual toggle always wins (sticky), else the column is collapsed when
  //    it holds no visible card ("empty -> closed" default);
  //  - during a drag the board turns into a compact row of targets: the SOURCE
  //    column stays open (its cards are the reorder context, and it must not
  //    collapse as the card temporarily leaves it) and EVERY other column stays
  //    collapsed — including the hovered one, which used to auto-expand as soon
  //    as the optimistic move made it non-empty. That expansion moved the
  //    geometry mid-gesture and pushed the other targets around; the drop-target
  //    banner carries the message instead, and manual/empty rules are ignored so
  //    the row stays stable for the whole drag.
  const isColCollapsedId = (colId) => {
    if (activeTask) return colId !== dragStartColumnRef.current;
    if (Object.prototype.hasOwnProperty.call(collapsedColumns, colId)) {
      return collapsedColumns[colId];
    }
    // Don't auto-collapse before tasks have loaded (avoids an initial collapsed
    // flash) nor on an empty project (keeps the columns open and inviting).
    if (tasksForFilters.length === 0) return false;
    return colTaskCounts[colId] === 0;
  };

  // Manual toggle stores an explicit override = inverse of the current
  // effective state, so it sticks against the empty-default afterwards.
  const toggleColumnCollapse = (columnId) => {
    setCollapsedColumns(prev => ({ ...prev, [columnId]: !isColCollapsedId(columnId) }));
  };

  // "Remonter en tête de colonne" from the card's arrow button
  // (KanbanMoveToTopButton, a reactive-json plugin inside the card YAML subroot).
  //
  // Deliberately walks the SAME path as a drop: same fractional rank between the
  // same neighbours, same optimistic array move (the array order IS the render
  // order — a rank alone would only show after a reload), same 10s landing ring,
  // same persist/revert. Nothing here is a second reordering mechanism.
  //
  // Neighbours are read from ALL the column's tasks, not the filtered ones, so
  // "top" means the top of the column and not the top of what a filter happens to
  // show — again, the drop's own convention.
  useEffect(() => {
    const handler = (e) => {
      const taskId = e.detail?.taskId;
      const task = tasksRef.current.find(t => String(t.data.id) === String(taskId));
      if (!task) return;
      // Read-only scopes: the card already hides the button in both cases, but the
      // bus is public — display decides what is offered, the board decides what is
      // done. Same split as everywhere else here.
      if (projectArchived || task.data.archived) return;
      const colId = effectiveColId(task, displayColColIdsRef.current);
      const columnTasks = tasksRef.current.filter(t => effectiveColId(t, displayColColIdsRef.current) === colId);
      const first = columnTasks[0];
      // Already on top → nothing to do (and generateKeyBetween(null, ownRank)
      // would happily mint a pointless key).
      if (!first || String(first.data.id) === String(task.data.id)) return;
      let newRank;
      try {
        newRank = generateKeyBetween(null, first.data.rank ?? null);
      } catch (err) {
        console.error('Rank computation failed:', err);
        return;
      }
      setTasks(prev => {
        const next = prev.map(t =>
          t.data.id === task.data.id ? { ...t, data: { ...t.data, rank: newRank } } : t
        );
        const from = next.findIndex(t => t.data.id === task.data.id);
        const firstIdx = next.findIndex(t => t.data.id === first.data.id);
        if (from === -1 || firstIdx === -1) return next;
        return arrayMove(next, from, firstIdx > from ? firstIdx - 1 : firstIdx);
      });
      setRecentMoveId(task.data.id);
      persistTask(task, { rank: newRank }, { statusOptions })
        .catch(err => {
          // No optimistic revert to hand-roll here: refetching restores the
          // server's own order, which is the only truth about ranks.
          console.error('Error persisting task:', err);
          setRecentMoveId(null);
          notifyRefusal(deriveNotifyFromError(err));
          refreshTasks();
        });
    };
    document.addEventListener('kanbanMoveToTop', handler);
    return () => document.removeEventListener('kanbanMoveToTop', handler);
  }, [statusOptions, projectArchived]);

  // Listen for subtask drill-down events dispatched by KanbanSubtaskButton
  // (a reactive-json plugin inside the card YAML subroot).
  useEffect(() => {
    const handler = (e) => {
      const taskId = e.detail?.taskId;
      const task = tasksRef.current.find(t => t.data.id === taskId);
      if (!task) return;
      const hasSubTypes = typeOptionsRef.current.some(o => Number(o.data?.parentId) === Number(task.data.type));
      if (hasSubTypes) {
        if (scrollContainerRef.current) {
          mainBoardScrollPos.current = scrollContainerRef.current.scrollLeft;
        }
        setSelectedParentTask(task);
        // The type filter now shows in the drill-down too, over the SUBTASK types
        // present under this parent. A filter left on a top-level type would match
        // none of them: the board would look empty while the select, finding no
        // matching option, would claim "Tous les types". Cross a scope, drop it.
        setSelectedType(null);
        setIsSubTaskMenuOpen(false);
      }
    };
    document.addEventListener('kanbanSubtaskDrilldown', handler);
    return () => document.removeEventListener('kanbanSubtaskDrilldown', handler);
  }, []);

  // Listen for delete events dispatched by KanbanDeleteButton.
  // We match by deleteUrl since the subroot doesn't hold a full task reference.
  useEffect(() => {
    const handler = (e) => {
      const { deleteUrl } = e.detail || {};
      if (!deleteUrl) return;
      const task = tasksRef.current.find(t => t.delete_url === deleteUrl);
      if (task) handleDeleteTask(task);
    };
    document.addEventListener('kanbanDeleteTask', handler);
    return () => document.removeEventListener('kanbanDeleteTask', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = async (e) => {
      const { taskId } = e.detail || {};
      if (!taskId) return;
      try {
        await archiveTask(taskId);
        // B1: keep the card in place (greyed, then hidden if the toggle is off)
        // instead of removing it; refreshTasks() below confirms from the server
        // and picks up any cascaded subtasks.
        setTasks(prev => prev.map(t => t.data.id === taskId
          ? { ...t, data: { ...t.data, archived: new Date().toISOString() } }
          : t));
        const projectId = globalDataContext?.data?.project?.id
          || new URLSearchParams(window.location.search).get('id')
          || null;
        window.dispatchEvent(new CustomEvent('task:entity-archived', {
          detail: { type: 'task', id: taskId, projectId: projectId ? parseInt(projectId) : null },
        }));
        refreshTasks();
      } catch (err) {
        console.error('Error archiving task:', err);
        const msg = err.message?.includes('403')
          ? 'Permission refusée. Vous n\'avez pas les droits pour archiver cette tâche.'
          : `Erreur d'archivage : ${err.message}`;
        alert(msg);
      }
    };
    document.addEventListener('kanbanArchiveTask', handler);
    return () => document.removeEventListener('kanbanArchiveTask', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Voir les archivées" toggle, flipped from KanbanHeader via a CustomEvent.
  useEffect(() => {
    const handler = (e) => setShowArchived(!!e.detail?.showArchived);
    document.addEventListener('kanbanShowArchivedChanged', handler);
    return () => document.removeEventListener('kanbanShowArchivedChanged', handler);
  }, []);

  // Désarchiver a task (server enforces admin / project-lead permission; the UI
  // only shows the action to those roles). Mirror of the archive handler.
  useEffect(() => {
    const handler = async (e) => {
      const { taskId } = e.detail || {};
      if (!taskId) return;
      try {
        await unarchiveTask(taskId);
        setTasks(prev => prev.map(t => t.data.id === taskId
          ? { ...t, data: { ...t.data, archived: null } }
          : t));
        refreshTasks();
      } catch (err) {
        console.error('Error unarchiving task:', err);
        const msg = err.message?.includes('403')
          ? 'Permission refusée. Seuls les administrateurs et chefs de projet peuvent désarchiver.'
          : `Erreur de désarchivage : ${err.message}`;
        alert(msg);
      }
    };
    document.addEventListener('kanbanUnarchiveTask', handler);
    return () => document.removeEventListener('kanbanUnarchiveTask', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for inline-edit changes dispatched by KanbanSyncBridge inside the
  // card subroots. This keeps KanbanBoard's `tasks` state in sync with the
  // subroots so that drag-and-drop (which re-renders cards from this state)
  // doesn't revert user edits.  No persistence is triggered here — the
  // DataSync component in KanbanCard.yaml handles that.
  useEffect(() => {
    const handler = (e) => {
      const { taskId, updates } = e.detail || {};
      if (!taskId || !updates) return;
      // The card subroot re-projects values into a slightly different shape than
      // the board stores them (assignee ''↔null, tags via refId, number↔string),
      // so KanbanSyncBridge can echo back "updates" that are NOT real edits.
      // Minting a new task object for such an echo creates a new dataOverride →
      // the reactive-json subroot re-seeds its whole store → the bridge re-fires
      // → infinite render loop ("Maximum update depth exceeded" in At3). Only
      // rebuild the task when a field has genuinely changed; otherwise keep the
      // SAME object/array references so React bails out and the loop cannot start.
      setTasks(prev => {
        const sameValue = (a, b) => {
          if (Array.isArray(a) || Array.isArray(b)) {
            const norm = (x) => JSON.stringify((Array.isArray(x) ? x : []).map(String));
            return norm(a) === norm(b);
          }
          return String(a ?? '') === String(b ?? '');
        };
        let changed = false;
        const next = prev.map(t => {
          if (t.data.id !== taskId) return t;
          if (Object.keys(updates).every(k => sameValue(t.data[k], updates[k]))) {
            return t;
          }
          changed = true;
          return { ...t, data: { ...t.data, ...updates } };
        });
        return changed ? next : prev;
      });
    };
    document.addEventListener('kanbanCardUpdated', handler);
    return () => document.removeEventListener('kanbanCardUpdated', handler);
  }, []);

  // T17 (CDC-TMA #53): a transition requested from a card's transition menu —
  // apply the new workflow state optimistically (like a drop) and persist;
  // revert on failure (e.g. T28 subtask-closure guard returns 422).
  useEffect(() => {
    const handler = (e) => {
      const { taskId, toState } = e.detail || {};
      if (!taskId || !toState) return;
      const task = (tasksRef.current || []).find(t => t.data.id === taskId);
      if (!task || task.data.state === toState) return;
      const fromState = task.data.state;
      const fromCol = task.data.columnId;
      // For PARENT (workflow) tasks the column id IS the state id, so move both.
      // For SUBTASKS the state id is NOT a board column id — update state only and
      // leave columnId untouched (writing columnId=toState would stamp a bogus column).
      const isChild = typeOptionsRef.current.some(o => String(o.value) === String(task.data.type) && Number(o.data?.parentId) > 0);
      setTasks(prev => prev.map(t =>
        t.data.id === taskId ? { ...t, data: { ...t.data, state: toState, ...(isChild ? {} : { columnId: toState }) } } : t
      ));
      persistTask(task, { state: toState }, { statusOptions })
        .then(() => {
          // CASCADE re-sync: a subtask reaching a closed state may unblock/advance
          // its PARENT server-side (ECA). Refetch only on closure so the parent's
          // new state shows; ordinary transitions keep the no-refetch fast path.
          if (isStateClosed(task.data.type, toState)) refreshTasks();
        })
        .catch((err) => {
          console.error('Transition failed:', err);
          setTasks(prev => prev.map(t =>
            t.data.id === taskId ? { ...t, data: { ...t.data, state: fromState, ...(isChild ? {} : { columnId: fromCol }) } } : t
          ));
          // recette 3: surface WHY the transition was refused (e.g. T28 subtask
          // guard returns 422) instead of silently reverting. Same bus as the drop.
          notifyRefusal(deriveNotifyFromError(err));
        });
    };
    document.addEventListener('kanbanTransitionRequest', handler);
    return () => document.removeEventListener('kanbanTransitionRequest', handler);
  }, [statusOptions]);


  const handleCreateSubTask = (tid) => {
      setSubTaskType(String(tid));
      setIsSubTaskMenuOpen(false);
      setIsSubTaskModalOpen(true);
  };

  const handleSaveSubTask = async (taskData) => {
      if (!selectedParentTask) return;

      let assigneeId = null;
      if (taskData.assignee) {
          // Resolve against members only, so a stale free-typed name cannot assign a non-member.
          const user = memberUsers.find(u => u.name === taskData.assignee);
          assigneeId = user ? user.id : null;
      }

      const projectId = globalDataContext?.data?.project?.id
        || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('id'))
        || null;

      const data = {
          title: taskData.title,
          type: resolveOptionTid(taskData.type, typeOptions),
          parentTaskId: selectedParentTask.data.id,
          // Workflow initial state is assigned server-side on create (presave →
          // first state of the type's workflow), so the client no longer sends it.
          assignee: assigneeId,
          tags: taskData.tags,
          returnType: taskData.returnType || null,
          evolutionType: taskData.evolutionType || null,
          description: taskData.description || null,
          projectId: projectId ? parseInt(projectId) : null,
          // Échéance (field_due_date, date-only) : la popin rend déjà du YYYY-MM-DD.
          dueDate: taskData.dueDate || null,
      };

      for (const key of ['assignee', 'returnType', 'evolutionType']) {
        if (data[key] === '') data[key] = null;
      }

      // priority/complexity OPTIONAL: OMIT when unset (sending 0 violates min:1 → 422).
      const p = parseInt(taskData.rating, 10);
      if (Number.isInteger(p) && p >= 1) data.priority = p;
      const c = parseInt(taskData.complexity, 10);
      if (Number.isInteger(c) && c >= 1) data.complexity = c;

      // Deferred file attachments (fids of temporary files uploaded in the modal).
      if (Array.isArray(taskData.attachedFiles) && taskData.attachedFiles.length) {
        data.attachedFiles = taskData.attachedFiles;
      }

      const createUrl = '/decoupled-api/save?display=decoupled&entity_type=node&bundle=task';
      try {
          await fetch(createUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ data }),
          });
          refreshTasks();
      } catch (error) {
          console.error("Error saving subtask:", error);
      }
  };

  const getSubTaskOptions = (parentTypeTid) =>
      typeOptions.filter(o => Number(o.data?.parentId) === Number(parentTypeTid));


  function formatArchiveDateKanban(isoStr) {
    if (!isoStr) return '';
    return new Date(isoStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  return (
    <ActionDependant {...props} attributesHolderRef={mainAttributesHolderRef}>
        {/* position: relative — the drop-target banner anchors to the TOP of the
            kanban (absolute, so it never pushes the board). */}
        <div {...attributes} style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
             {/* Accessible toast host (kanbanNotify bus). Mounted once, in every
                 view mode, so drop/transition refusals always have somewhere to
                 render. Fixed-positioned, so it doesn't affect the board layout. */}
             <KanbanToast />
             {/* Bannière lecture seule — projet archivé */}
             {projectArchived && (
               <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm shrink-0">
                 <Archive size={16} className="shrink-0 text-amber-600" />
                 <span>
                   <strong>Projet archivé</strong> — Archivé le {formatArchiveDateKanban(projectArchived)}. Ce projet et ses tâches sont en <strong>lecture seule</strong>.
                 </span>
               </div>
             )}
             {/* Integrated View Switcher & Filters */}
             <div className="flex flex-col gap-2 mb-4 px-1">
                {selectedParentTask && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <button 
                            onClick={() => {
                                setShouldRestoreScroll(true);
                                setSelectedParentTask(null);
                                // Same reason as on the way in: a subtask type
                                // would match no top-level task back on the board.
                                setSelectedType(null);
                            }}
                            className="hover:text-iris-600 flex items-center gap-1 font-medium transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                            Retour au projet
                        </button>
                        <span className="text-slate-300">/</span>
                        <span className="text-slate-800 font-semibold flex items-center gap-2">
                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border bg-green-100 text-green-700 border-green-200">
                                {resolveTypeLabel(selectedParentTask.data.type, typeOptions) || 'Tâche'}
                            </span>
                            {selectedParentTask.data.title}
                        </span>
                        
                        {/* Sub-task Creation Button */}
                        <div className="relative ml-4">
                             <button 
                                onClick={() => setIsSubTaskMenuOpen(!isSubTaskMenuOpen)}
                                className="flex items-center gap-1.5 bg-iris-100 hover:bg-iris-200 text-iris-700 px-3 py-1.5 rounded-full transition-colors text-xs font-semibold"
                                title="Créer une sous-tâche"
                             >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                Créer une sous-tâche
                             </button>
                             {isSubTaskMenuOpen && (
                                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-slate-200 z-50 py-1">
                                    {getSubTaskOptions(selectedParentTask.data.type).map(opt => (
                                        <button
                                            key={opt.value}
                                            className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-iris-50 hover:text-iris-700"
                                            onClick={() => handleCreateSubTask(opt.value)}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                             )}
                        </div>
                    </div>
                )}
                <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-4 flex-wrap">
                     {/* Type Filter */}
                     {!props.isSubTaskView && (
                     <div className="relative">
                         {/* Also shown in drill-down: workflowTaskTypes is then the
                             SUBTASK types present under this parent, so the filter
                             offers exactly what the board holds. */}
                         <select
                            className="select"
                            value={selectedType || ''}
                            onChange={(e) => setSelectedType(e.target.value || null)}
                         >
                             <option value="">Tous les types</option>
                             {workflowTaskTypes.map(tt => (
                                 <option key={tt.data.id} value={tt.data.id}>{tt.data.name}</option>
                             ))}
                         </select>
                     </div>
                     )}

                     {/* Assignee filter — multi-select autocomplete over the project members only. */}
                     <KanbanAssigneeFilter
                         users={memberUsers}
                         value={selectedAssignees}
                         onChange={setSelectedAssignees}
                     />

                     {/* Recherche plein texte — même barre que les filtres
                         type / assigné, et restreint les deux vues. */}
                     <div className="flex items-center gap-2">
                         <label htmlFor={searchFieldId} className="text-sm text-slate-500 whitespace-nowrap">Rechercher :</label>
                         <div className="relative">
                             <Search size={16} aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                             <input
                                id={searchFieldId}
                                type="text"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Titre, tag, statut…"
                                className="input w-56 pl-9"
                             />
                         </div>
                     </div>

                     {/* Échéance — « à faire avant le » (date incluse) + raccourcis.
                         Les raccourcis écrivent dans le MÊME champ date : ce
                         qu'ils font reste lisible, et l'utilisateur peut ensuite
                         l'ajuster. */}
                     <div className="flex items-center gap-2 flex-wrap">
                         <label htmlFor={dueBeforeFieldId} className="text-sm text-slate-500 whitespace-nowrap">À faire avant le :</label>
                         <input
                            id={dueBeforeFieldId}
                            type="date"
                            value={dueBefore}
                            onChange={(e) => setDueBefore(e.target.value)}
                            className="input w-40"
                         />
                         {/* Le filtre retire des cartes sans rien dire : la croix
                             est la sortie. À CÔTÉ du champ et non dedans — un
                             input[type=date] y loge déjà son icône de calendrier.
                             Elle disparaît quand il n'y a rien à effacer, jamais
                             en restant tabulable et invisible (WCAG 2.4.7). */}
                         {!!dueBefore && (
                           <button
                              type="button"
                              onClick={() => setDueBefore('')}
                              title="Effacer l'échéance"
                              aria-label="Effacer le filtre d'échéance"
                              className="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                           >
                              <X size={15} aria-hidden="true" />
                           </button>
                         )}
                         <div className="flex items-center gap-1">
                             {[
                               { label: "aujourd'hui", value: todayIso() },
                               { label: 'dans la semaine', value: isoFromToday({ days: 7 }) },
                               { label: 'dans le mois', value: isoFromToday({ months: 1 }) },
                             ].map(({ label, value }) => (
                               <button
                                  key={label}
                                  type="button"
                                  aria-pressed={dueBefore === value}
                                  onClick={() => setDueBefore(dueBefore === value ? '' : value)}
                                  className={`text-xs underline underline-offset-2 rounded px-1 py-0.5 transition-colors ${
                                    dueBefore === value ? 'text-iris-700 font-semibold' : 'text-slate-500 hover:text-iris-600'}`}
                               >
                                  {label}
                               </button>
                             ))}
                         </div>
                     </div>
                </div>

                <div className="flex items-center border border-slate-300 rounded overflow-hidden bg-white shadow-sm ml-auto">
                  {viewMode !== 'list' && (
                  <button
                    onClick={() => setShowCardDetails(!showCardDetails)}
                    className={`p-2 border-r border-slate-300 ${!showCardDetails ? 'bg-slate-100 text-iris-700' : 'text-slate-400 hover:bg-slate-50'}`}
                    title={showCardDetails ? "Masquer les détails" : "Afficher les détails"}
                  >
                     {showCardDetails ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                     ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                     )}
                  </button>
                  )}
                  <button 
                    onClick={() => setViewMode('board')}
                    className={`p-2 border-r border-slate-300 ${viewMode === 'board' ? 'bg-slate-100 text-iris-700' : 'text-slate-400 hover:bg-slate-50'}`}
                    title="Board View"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  </button>
                  <button 
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-slate-100 text-iris-700' : 'text-slate-400 hover:bg-slate-50'}`}
                    title="List View"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  </button>
                </div>
             </div>
             </div>
             
             {(!props.isSubTaskView && scopeHasNoTask) ? (
                 /* Nothing in scope: no task → no present type → no column (by
                    design). Same state for an empty project and for a parent
                    without a subtask — only the wording and the CTA differ.
                    Neither CTA duplicates any creation logic: the project one
                    fires `kanbanOpenNewTask` on the shared kanban* bus and
                    KanbanHeader opens its own type dropdown; the drill-down one
                    opens the "+ Nouvelle sous-tâche" menu that already sits in the
                    breadcrumb above. */
                 <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
                   <div className="w-16 h-16 rounded-full bg-iris-50 flex items-center justify-center mb-4">
                     <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-iris-300" aria-hidden="true">
                       <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                     </svg>
                   </div>
                   <p className="text-slate-500 text-sm mb-1">
                     {selectedParentTask ? 'Aucune sous-tâche pour cette tâche' : 'Aucune tâche dans ce projet'}
                   </p>
                   <p className="text-slate-400 text-xs mb-6">
                     {selectedParentTask
                       ? 'Créez une première sous-tâche : les colonnes du kanban s\'afficheront selon son type.'
                       : 'Créez une première tâche : les colonnes du kanban s\'afficheront selon son type.'}
                   </p>
                   <button
                     type="button"
                     onClick={() => selectedParentTask
                       ? setIsSubTaskMenuOpen(true)
                       : document.dispatchEvent(new CustomEvent('kanbanOpenNewTask'))}
                     disabled={!!projectArchived}
                     title={projectArchived ? 'Projet archivé — création désactivée' : undefined}
                     className="btn btn-primary"
                   >
                     <span className="font-bold text-lg leading-none pb-1">+</span>
                     <span>{selectedParentTask ? 'Nouvelle sous-tâche' : 'Nouveau'}</span>
                   </button>
                 </div>
             ) : viewMode === 'list' ? (
                 <div className="flex-grow overflow-y-auto p-6">
                   <KanbanListView tasks={listTasks} columns={displayColumns} tagOptions={tagOptions} returnTypeOptions={returnTypeOptions} typeOptions={typeOptions} users={users} assignableUsers={memberUsers} />
                 </div>
             ) : (
                 <div
                    ref={scrollContainerRef}
                    // Sub-task view (inside the TaskEdit tab) sizes via flex, not an
                    // explicit-height ancestor chain, so h-full would collapse to 0;
                    // flex-1 + min-h-0 fills the available space there. The main
                    // board keeps h-full (its page wrapper has a definite height).
                    className={`flex flex-col overflow-x-auto ${props.isSubTaskView ? 'flex-1 min-h-0' : 'h-full'}`}
                 >
                     <DndContext
                        sensors={sensors}
                        collisionDetection={collisionDetection}
                        measuring={measuringConfig}
                        onDragStart={onDragStart}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        onDragOver={onDragOver}
                        onDragCancel={onDragCancel}
                     >
                        <div className="flex gap-2 sm:gap-3 items-start pb-4">
                            {(() => {
                              const activeWf = activeTask ? getWorkflowForType(activeTask.data.type, workflowTaskTypes) : null;
                              const activeHasWorkflow = !!activeWf?.transitions;
                              return displayColumns.map((col) => {
                                // Forbidden via the FULL policy pre-filter (workflow reachability +
                                // role permission + open-subtask guard) resolved by forbiddenMotif,
                                // which returns a short FR motif string for the overlay or false.
                                // '__no_state__' on a workflow-managed task is always forbidden
                                // (clearing the state would crash the validator). Only evaluated
                                // during an active drag; null when nothing is being dragged.
                                const isForbidden = activeTask
                                    ? forbiddenMotif(activeTask, col.id, activeHasWorkflow)
                                    : false;
                                // Green = "this card may land here". The card's own column is
                                // not a destination: forbiddenMotif never flags it (a card may
                                // always come home), but it isn't a move either. Keyed on the
                                // stable `state`, never on columnId — onDragOver rewrites
                                // columnId to every column the card flies over.
                                const isAllowed = !!activeTask && !isForbidden
                                    && col.id !== activeTask.data?.state;
                                // Colour of the collapsed strips during a drag (dropToneClass):
                                // a column outside this type's workflow is not "refused", it
                                // simply doesn't concern this card — keeping red for real
                                // refusals is what makes red readable on a multi-type board.
                                const dropTone = !activeTask
                                    ? null
                                    : !activeWf?.workflowStates?.[col.id] ? 'outside'
                                    : col.id === activeTask.data?.state ? 'current'
                                    : isForbidden ? 'refused'
                                    : 'allowed';

                                return (
                                <Column
                                    key={col.id}
                                    column={col}
                                    tasks={filteredTasks.filter((task) => effectiveColId(task, displayColColIds) === col.id)}
                                    showCardDetails={showCardDetails}
                                    isDropForbidden={isForbidden}
                                    isDropAllowed={isAllowed}
                                    dropTone={dropTone}
                                    recentMoveId={recentMoveId}
                                    allTasks={tasks}
                                    isCollapsed={isColCollapsedId(col.id)}
                                    toggleCollapse={toggleColumnCollapse}
                                    tagOptions={tagOptions}
                                    returnTypeOptions={returnTypeOptions}
                                    typeOptions={typeOptions}
                                    users={users}
                                />
                                );
                              });
                            })()}
                        </div>

                        {/* Inside the DndContext: it tracks dnd-kit's own `over`.
                            Always mounted, so it never misses the first hover. */}
                        <DropTargetBanner
                          activeTask={activeTask}
                          workflowTaskTypes={workflowTaskTypes}
                          columns={displayColumns}
                        />

                        {/* dropAnimation={null}: every column re-expands in the drop
                            commit, so the default animation would fly the card across
                            the whole board towards its target column's NEW position. */}
                        {typeof document !== 'undefined' && <DragOverlay dropAnimation={null}>
                            {activeTask && (
                              <DragPreviewCard
                                task={activeTask}
                                users={users}
                                tagOptions={tagOptions}
                                returnTypeOptions={returnTypeOptions}
                                typeOptions={typeOptions}
                                showDetails={showCardDetails}
                              />
                            )}
                        </DragOverlay>}
                    </DndContext>
                 </div>
             )}
             
             <NewTaskModal
                isOpen={isSubTaskModalOpen}
                onClose={() => setIsSubTaskModalOpen(false)}
                onSave={handleSaveSubTask}
                taskType={subTaskType}
                isSubTask={true}
                users={memberUsers}
                returnTypeOptions={returnTypeOptions}
                evolutionTypeOptions={evolutionTypeOptions}
                tagOptions={tagOptions}
                typeOptions={subtaskTypes}
             />
        </div>
    </ActionDependant>
  );
};
